import { useVisitorData } from '@fingerprintjs/fingerprintjs-pro-react';
import { usePrivy } from '@privy-io/react-auth';
import { useMemo } from 'react';
import { useShallow } from 'zustand/react/shallow';

import config from '@/config';
import { usePrivyAccount } from '@/hooks';
import { useAccountStore } from '@/store/use-account-store';

export function useFingerprintTag() {
  const { user } = usePrivy();
  const { authenticated, address: primaryAddress } = usePrivyAccount();
  const activeAddress = useAccountStore((state) => state.address);
  const capitalStatusByAddress = useAccountStore(
    useShallow((state) => state.capitalStatusByAddress),
  );

  const hasPrimaryCapitalStatus = useMemo(() => {
    if (!primaryAddress) {
      return false;
    }

    return primaryAddress.toLowerCase() in capitalStatusByAddress;
  }, [primaryAddress, capitalStatusByAddress]);

  const tag = useMemo(() => {
    return authenticated &&
      user &&
      primaryAddress &&
      activeAddress &&
      hasPrimaryCapitalStatus
      ? { privyId: user.id, primaryAddress, activeAddress }
      : undefined;
  }, [
    authenticated,
    user,
    primaryAddress,
    activeAddress,
    hasPrimaryCapitalStatus,
  ]);

  return { tag, primaryAddress };
}

export function useFingerprint() {
  const { user } = usePrivy();
  const { authenticated, address: primaryAddress } = usePrivyAccount();

  const { getData } = useVisitorData(
    { extendedResult: true },
    { immediate: false },
  );

  const refetchRequestId = async (): Promise<string | undefined> => {
    if (config.isLocal) {
      return undefined;
    }

    const activeAddress = useAccountStore.getState().address;

    if (!authenticated || !user || !primaryAddress || !activeAddress) {
      return undefined;
    }

    try {
      const data = await getData({
        ignoreCache: true,
        tag: { privyId: user.id, primaryAddress, activeAddress },
        linkedId: primaryAddress,
      });

      if (data?.visitorId) {
        localStorage.setItem('fpjs_visitor_id', data.visitorId);
      }

      if (data?.requestId) {
        localStorage.setItem('fpjs_request_id', data.requestId);
        useAccountStore.getState().setFingerprintRequestId(data.requestId);
        return data.requestId;
      }
    } catch {} // eslint-disable-line no-empty

    return undefined;
  };

  return { refetchRequestId };
}
